'use client';

import Image from 'next/image';
import { Box } from '@mui/material';

export type MarketingLayer = {
  src: string;
  alt: string;
};

type Props = {
  layers: MarketingLayer[];
  /** Mirror fan direction when image column is on the left */
  mirror?: boolean;
};

/**
 * Cascade steps sized for short marketing crops (~90–170px tall).
 * Prior values (~28×24) only left a paper-thin edge — looked like one card.
 * Each step now peeks enough UI that you can tell layers apart.
 */
function fanForIndex(i: number) {
  return {
    x: i * 56,
    y: i * 64,
    rotate: -2 + i * 1.1,
    scale: 0.92 + i * 0.03,
    z: i + 1,
  };
}

/**
 * Layered UI cards — diagonal cascade so every panel stays readable.
 */
export function MarketingLayeredStack({ layers, mirror = false }: Props) {
  const count = Math.max(layers.length, 1);
  // Last card top + approximate crop height + breathing room
  const lastY = (count - 1) * 64;
  const stageH = {
    xs: lastY + 200,
    md: lastY + 220,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: stageH,
        py: { xs: 2, md: 3 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 360, md: 520 },
          minHeight: stageH,
          // Room on the opposite side of the fan so translateX does not clip
          px: { xs: 1, md: 2 },
        }}
      >
        {layers.map((layer, i) => {
          const fan = fanForIndex(i);
          const offsetX = mirror ? -fan.x : fan.x;
          const rotate = mirror ? -fan.rotate : fan.rotate;
          return (
            <Box
              key={layer.src}
              sx={{
                position: 'absolute',
                top: fan.y,
                left: mirror ? undefined : { xs: 4, md: 8 },
                right: mirror ? { xs: 4, md: 8 } : undefined,
                transform: `translateX(${offsetX}px) rotate(${rotate}deg) scale(${fan.scale})`,
                transformOrigin: mirror ? 'top right' : 'top left',
                zIndex: fan.z,
                width: { xs: '82%', md: '78%' },
                maxWidth: 380,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                boxShadow: '0 14px 36px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.06)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, z-index 0s',
                '&:hover': {
                  zIndex: 20,
                  transform: `translateX(${offsetX}px) rotate(${rotate}deg) scale(${Math.min(fan.scale + 0.04, 1.06)})`,
                  boxShadow: '0 18px 44px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.08)',
                },
              }}
            >
              <Image
                src={layer.src}
                alt={layer.alt}
                width={366}
                height={140}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
